<template>
  <modal
    :name="$lang.messages.personalInfoModal"
    :adaptive="true"
    height="auto"
    :width="500"
    transition="linear"
    id="personalInfo"
    ref="personalInfo"
  >
    <form
      name="personalInfoForm"
      class="fp-modal"
      novalidate
      @submit.prevent
      autocomplete="off"
    >
      <modals-header
        :modalNameProp="$lang.messages.personalInfoModal"
        @hideModalProp="hidePersonalInfoModal"
      >
      </modals-header>

      <hr class="v--modal-hr">

      <input autocomplete="false" name="hidden" type="text" class="hidden">
      
      <div v-if="step1">
        <!-- FIRST NAME -->
        <div class="part">
          <p class="label input-filter-label">
            {{ $lang.messages.firstName }}
            <span class="asterix">*</span>
          </p>
          <div class="input-wrapper input-field" :class="{'error-input' : errors.has('personalInfo.firstName')}">
            <input
                type="text"
                name="firstName"
                class="input"
                :placeholder="$lang.messages.enterFirstName"
                v-model="personalInfo.firstName"
                data-vv-scope="personalInfo"
                v-validate="'required|min:1|max:32'"
                @click.self="clearInputErrors($event)"
                @focus.self="clearInputErrors($event)"
                @blur="clearOthers($event)"
            />
            <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon" />
            <div
                v-show="errors.has('personalInfo.firstName')"
                class="error-tooltip"
                :class="{'hide-tooltip' : bluredProp != 'firstName'}"
            >
              <div class="text">
                {{ errors.first('personalInfo.firstName') }}
              </div>
            </div>
          </div>
        </div>
  
        <!-- LAST NAME -->
        <div class="part">
          <p class="label input-filter-label">
            {{ $lang.messages.lastName }}
            <span class="asterix">*</span>
          </p>
          <div class="input-wrapper input-field" :class="{'error-input' : errors.has('personalInfo.lastName')}">
            <input
                type="text"
                name="lastName"
                class="input"
                :placeholder="$lang.messages.enterLastName"
                v-model="personalInfo.lastName"
                data-vv-scope="personalInfo"
                v-validate="'required|min:1|max:32'"
                @click.self="clearInputErrors($event)"
                @focus.self="clearInputErrors($event)"
                @blur="clearOthers($event)"
            />
            <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon" />
            <div
                v-show="errors.has('personalInfo.lastName')"
                class="error-tooltip"
                :class="{'hide-tooltip' : bluredProp !== 'lastName'}"
            >
              <div class="text">
                {{ errors.first('personalInfo.lastName') }}
              </div>
            </div>
          </div>
        </div>
  
        <!-- DATE OF BIRTH-->
        <div class="part short-part datepicker-part">
          <p class="label input-filter-label">
            {{ $lang.messages.dateOfBirth }}
          </p>
          <f-p-datepicker
              :dateOfBirth="true"
              :option="personalInfo.birthDate.toString()"
              :initialBlank="personalInfo.birthDate === ''"
              :forbidFuture="true"
              @optionChanged="onSelectedBirthDate" >
          </f-p-datepicker>
        </div>
  
        <!-- COUNTRY -->
        <div class="part">
          <p class="label input-filter-label">
            {{ $lang.messages.country }}
            <span class="asterix">*</span>
          </p>
          <div class="wrapper">
            <autocomplete
                v-if="countries.length !== 0 && personalInfo.country.code !== ''"
                :items="countries"
                :placeholder="$lang.messages.enterCountry"
                :option="personalInfo.country"
                :name="'country'"
                :scope="'personalInfo'"
                :bluredProp="bluredProp"
                @optionChanged="onSelectedCountry"
                @clearInputErrors="clearInputErrors"
                @clearOthers="clearOthers"
                :key="rerenderCountry"
                ref="autocompleteCountry"
            />
          </div>
        </div>
  
        <!-- PHONE NUMBER -->
        <div class="part">
          <p class="label input-filter-label">
            {{ $lang.messages.phoneNumber }}
            <span class="asterix">*</span>
          </p>
          <div class="input-wrapper input-field" :class="{'error-input' : errors.has('personalInfo.phoneNumber')}">
            <input
                type="text"
                name="phoneNumber"
                class="input"
                :placeholder="$lang.messages.enterPhoneNumber"
                v-model="personalInfo.phoneNumber"
                data-vv-scope="personalInfo"
                v-validate="'required'"
                @click.self="clearInputErrors($event)"
                @focus.self="clearInputErrors($event)"
                @blur="clearOthers($event)"
            />
            <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon" />
            <div
                v-show="errors.has('personalInfo.phoneNumber')"
                class="error-tooltip"
                :class="{'hide-tooltip' : bluredProp != 'phoneNumber'}"
            >
              <div class="text">
                {{ errors.first('personalInfo.phoneNumber') }}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="step2">
        <!-- LABEL -->
        <div class="part">
          <p class="label input-filter-label">
            {{ $lang.messages.phoneNumberVerificationNote1 }} <strong>{{ this.personalInfo.phoneNumber }}</strong> {{ $lang.messages.phoneNumberVerificationNote2 }}
          </p>
          <br>
        </div>
        
        <!-- CODE -->
        <div class="part">
          <p class="label input-filter-label">
            {{ $lang.messages.code }}
            <span class="asterix">*</span>
          </p>
          <div class="input-wrapper input-field" :class="{'error-input' : errors.has('personalInfo.code')}">
            <input
                type="text"
                name="code"
                class="input"
                :placeholder="$lang.messages.enterCode"
                v-model="personalInfo.code"
                data-vv-scope="personalInfo"
                v-validate="'required'"
                @click.self="clearInputErrors($event)"
                @focus.self="clearInputErrors($event)"
                @blur="clearOthers($event)"
            />
            <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon" />
            <div
                v-show="errors.has('personalInfo.code')"
                class="error-tooltip"
                :class="{'hide-tooltip' : bluredProp != 'code'}"
            >
              <div class="text">
                {{ errors.first('personalInfo.code') }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="part">
        <p class="label input-filter-label">
          <span class="asterix">*</span> {{ $lang.messages.mandatoryFields }}
        </p>
      </div>

      <!-- CLOSE MODAL -->
      <button
        type="button"
        class="generic-btn"
        @click="hidePersonalInfoModal"
      >
        {{ $lang.messages.back }}
      </button>

      <!-- SUBMIT -->
      <button
        type="button"
        class="action-btn"
        @click="onPersonalInfoFormSubmitted"
      >
        <span v-show="!loading">{{ $lang.messages.save }}</span>
        <ClipLoader :loading="loading" :color="'#fff'" :size="'20px'"></ClipLoader>
      </button>

    </form>
  </modal>
</template>

<script>
  import { mapActions } from "vuex";
  import { mapMutations } from "vuex";
  import { EDIT_PERSONAL_INFO, GET_COUNTRIES, VERIFY_PHONE_NUMBER_ON_PROFILE } from "@/store/action.types";
  import { SET_USER } from "@/store/mutation.types";
  import { dateFormat, requestDateFormatUTC, requestDateFormat } from '@/mixins/dateFormat';
  import Autocomplete from "@/components/common/Autocomplete";
  import modalsHeader from "@/components/common/modals/ModalsHeader";
  import FPDatepicker from '@/components/common/FPDatepicker';
  import ClipLoader from 'vue-spinner/src/ClipLoader';
  import STATUS_CODES from "@/common/statusCodes";

  export default {
    name: "personalInfo",
    inject: ['$validator'],
    components: {
      Autocomplete,
      'modals-header': modalsHeader,
      ClipLoader,
      FPDatepicker
    },
    mixins: [
      dateFormat,
      requestDateFormatUTC,
      requestDateFormat
    ],
    props: {
      personalInfoData: {
        type: Object,
        required: true,
        default: () => {}
      }
    },
    data() {
      return {
        typeable: true,
        countries: [],
        rerenderCountry: 0,
        personalInfoFormSubmitted: false,
        personalInfo: {
          firstName: '',
          lastName: '',
          birthDate: '',
          country: {
            name: '',
            code: ''
          },
          phoneNumber: '',
          code: ''
        },
        bluredProp: '',
        loading: false,
        step1: true,
        step2: false
      };
    },
    mounted() {
      this.loadCountries()
    },
    methods: {
      ...mapMutations({
        userMutation: SET_USER
      }),
      ...mapActions({
        editPersonalInfoAction: EDIT_PERSONAL_INFO,
        getCountriesAction: GET_COUNTRIES,
        verifyPhoneNumberAction: VERIFY_PHONE_NUMBER_ON_PROFILE
      }),
      show() {
        this.step1 = true;
        this.step2 = false;
        this.generatePersonalInfoData(this.personalInfoData)
        this.$modal.show(this.$lang.messages.personalInfoModal)
        setTimeout(() => document.getElementById('personalInfo').addEventListener('click', this.handleClick), 0);
      },
      hidePersonalInfoModal() {
        this.checkAnyInput()
        this.personalInfoFormSubmitted = false
        this.$modal.hide(this.$lang.messages.personalInfoModal)
      },
      loadCountries() {
        this.getCountriesAction()
        .then(response => {
          this.countries = response.data.data.map(item => ({ name: item.countryName, code: item.countryCode }))
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        });
      },
      onSelectedCountry(value) {
        let countryValid = false;
        this.countries.forEach(element => {
          if (element.name === value.name) {
            countryValid = true
          }
        });

        if (countryValid === true) {
          this.personalInfo.country = value
        } else {
          this.personalInfo.country = {}
        }
      },
      onSelectedBirthDate(value) {
        this.personalInfo.birthDate = value;
      },
      generatePersonalInfoData(data) {
        this.personalInfo.firstName = data.firstName;
        this.personalInfo.lastName = data.lastName;

        if (data.dateOfBirth !== null) {
          this.personalInfo.birthDate = this.requestDateFormat(data.dateOfBirth);
        }
        let currentCountry = this.countries.find(function(element) {
          return element.name === data.country;
        })
        if (currentCountry) {
          this.personalInfo.country.name = currentCountry.name;
          this.personalInfo.country.code = currentCountry.code;
        }
        this.personalInfo.phoneNumber = data.phoneNumber;
        this.$validator.reset();
        this.checkAnyInput();
      },
      onPersonalInfoFormSubmitted() {
        if (!this.loading) {
          this.$validator.validateAll('personalInfo').then(result => {
            this.personalInfoFormSubmitted = true;

            if (this.step1) {
              this.editPersonalInfo(result);
            } else if (this.step2) {
              this.verifyPhoneNumber(result);
            }
          });
        }
      },
      editPersonalInfo(result) {
        if (this.personalInfo.country.name === undefined || this.personalInfo.country.name === '') {
          this.$refs.autocompleteCountry.createError()
        } else if (result) {
          this.loading = true;
          this.personalInfo.birthDate = this.requestDateFormatUTC(this.personalInfo.birthDate);
          this.editPersonalInfoAction({
            firstName: this.personalInfo.firstName,
            lastName: this.personalInfo.lastName,
            birthDate: this.personalInfo.birthDate === 'Invalid Date' ? null : this.personalInfo.birthDate,
            countryCode: this.personalInfo.country.code,
            phoneNumber: this.personalInfo.phoneNumber
          })
          .then(response => {
            let user = {
              firstname: response.data.data.firstName,
              lastname: response.data.data.lastName
            };
            this.userMutation(user);
            this.loading = false;
            if (response.data.data.phoneNumber !== this.personalInfo.phoneNumber) {
              this.step1 = false;
              this.step2 = true;
            }
            if (this.step1) {
              this.hidePersonalInfoModal();
              this.loadUserProfile();
              this.$snotify.success(this.$lang.messages.sn_successPersonalInfoEdited, "SUCCESS");
            }
            if (this.step2) {
              this.loadUserProfile();
            }
      
          })
          .catch(error => {
            this.loading = false;
            this.hidePersonalInfoModal();
            if (error.response.data) {
              // Handle error if user is younger than 16 years
              if (error.response.status === 400) {
                this.$snotify.error(error.response.data.status.desc, "ERROR")
                return
              }
              // Handle error if user have too long name
              this.$snotify.error(this.$lang.messages.sn_tooLongName, "ERROR")
              return
            }
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(error.response.data.status.desc, "ERROR")
            }
          });
        }
      },
      verifyPhoneNumber(result) {
        if (result) {
          this.loading = true;
          this.verifyPhoneNumberAction({
            phoneNumber: this.personalInfo.phoneNumber,
            smsCode: this.personalInfo.code
          })
          .then(() => {
            this.loading = false;
            this.hidePersonalInfoModal();
            this.loadUserProfile();
            this.$snotify.success(this.$lang.messages.sn_successPersonalInfoEdited, "SUCCESS");
          })
          .catch(error => {
            this.loading = false;
            this.hidePersonalInfoModal();
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(error.response.data.status.desc, "ERROR")
            }
          });
        }
      },
      loadUserProfile() {
        this.$emit("loadUserProfile");
      },
      clearInputErrors(e) {
        if (e.target) {
          this.$validator.errors.remove('personalInfo.' + e.target.name)
        } else {
          this.$validator.errors.remove('personalInfo.' + e)
        }
      },
      checkAnyInput() {
        this.bluredProp = ''
      },
      clearOthers(e) {
        this.bluredProp = e.target.name
      },
      /* DISABLED - need to find solution for vee-validate inside modals */
      // Update the input value we are sending to FPInputTextValidate
      // inputTextValueFirstName (val) {
      //   this.personalInfo.firstName = val
      // },
      // inputTextValueLastName (val) {
      //   this.personalInfo.lastName = val
      // },
      handleClick(e) {
        // Check if the modal is destoyed, hence the autocomplete field exist
        // only then validate the autocomplete
        if (this.$refs.autocompleteCountry) {
          this.$refs.autocompleteCountry.handleClickOutside(e)
          // this.$refs.autocompleteCountry.onOutsideClickModal('personalInfo', this.$refs.autocompleteCountry.name)
        }

      },
    }
  };
</script>
